import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbPhone, TbMail, TbDeviceMobile, TbBrandWhatsapp } from 'react-icons/tb';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import ActionButton from '../../../atoms/ActionButton';
import ConfirmationModal from '../../../templates/ConfirmationModal';

const BASE_URL = `${process.env.REACT_APP_PERSON_API_URL}/individual`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

interface Contact {
    id?: string;
    type: string;
    content: string;
    isActive: boolean;
    isDefault: boolean;
    isNationalNumber?: boolean;
}

interface Props {
    individualId: string;
    contacts: Contact[];
    onContactsChange: (contacts: Contact[]) => void;
}

const IndividualFormContacts: React.FC<Props> = ({ 
    individualId, 
    contacts, 
    onContactsChange 
}) => {
    const isModalOpen = useSignal<boolean>(false);
    const isDeleteModalOpen = useSignal<boolean>(false);
    const isLoading = useSignal<boolean>(false);
    
    const [editingContact, setEditingContact] = useState<Contact | null>(null);
    const [contactToDelete, setContactToDelete] = useState<string | null>(null);
    const [form, setForm] = useState<Contact>({
        type: 'EMAIL',
        content: '',
        isActive: true,
        isDefault: false,
        isNationalNumber: false
    });

    const contactTypes = [
        { value: 'EMAIL', label: 'E-mail' },
        { value: 'PHONE', label: 'Telefone Fixo' },
        { value: 'MOBILE', label: 'Celular' },
        { value: 'WHATSAPP', label: 'WhatsApp' }
    ];

    const getContactIcon = (type: string) => {
        switch (type) {
            case 'EMAIL': return <TbMail className="text-blue-600" />;
            case 'PHONE': return <TbPhone className="text-green-600" />;
            case 'MOBILE': return <TbDeviceMobile className="text-purple-600" />;
            case 'WHATSAPP': return <TbBrandWhatsapp className="text-green-500" />;
            default: return <TbPhone className="text-gray-600" />;
        }
    };

    const getContactTypeLabel = (type: string) => {
        const typeObj = contactTypes.find(t => t.value === type);
        return typeObj ? typeObj.label : type;
    };

    const openModal = (contact?: Contact) => {
        if (contact) {
            setEditingContact(contact);
            setForm(contact);
        } else {
            setEditingContact(null);
            setForm({
                type: 'EMAIL',
                content: '',
                isActive: true,
                isDefault: false,
                isNationalNumber: false
            });
        }
        isModalOpen.value = true;
    };

    const closeModal = () => {
        isModalOpen.value = false;
        setEditingContact(null);
        setForm({
            type: 'EMAIL',
            content: '',
            isActive: true,
            isDefault: false,
            isNationalNumber: false
        });
    };

    const validateForm = () => {
        if (!form.content.trim()) {
            toast.error('Preencha o conteúdo do contato');
            return false;
        }

        // Validate email format
        if (form.type === 'EMAIL') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(form.content)) {
                toast.error('Formato de e-mail inválido');
                return false;
            }
        }

        // Validate phone formats
        if (['PHONE', 'MOBILE', 'WHATSAPP'].includes(form.type)) {
            const phoneClean = form.content.replace(/\D/g, '');
            if (phoneClean.length < 10 || phoneClean.length > 15) {
                toast.error('Formato de telefone inválido');
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        isLoading.value = true;
        try {
            const url = editingContact 
                ? `${BASE_URL}/${individualId}/contacts/${editingContact.id}`
                : `${BASE_URL}/${individualId}/contacts`;
            
            const method = editingContact ? 'PUT' : 'POST';
            
            // ✅ CORREÇÃO: Obter JWT token do Amplify (igual aos Endereços)
            const { Auth } = await import('@aws-amplify/auth');
            const session = await Auth.currentSession();
            const token = session.getAccessToken().getJwtToken();
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`  // ✅ Token do Amplify
                },
                body: JSON.stringify({
                    type: form.type,
                    content: form.content,
                    is_active: form.isActive,
                    is_default: form.isDefault,
                    is_national_number: form.isNationalNumber
                })
            });

            if (response.ok) {
                toast.success(editingContact ? 'Contato atualizado!' : 'Contato cadastrado!');
                await loadContacts();
                closeModal();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao salvar contato');
            }
        } catch (error) {
            toast.error('Erro ao salvar contato');
            console.error('Error saving contact:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleDelete = async () => {
        if (!contactToDelete) return;

        isLoading.value = true;
        try {
            // ✅ CORREÇÃO: Obter JWT token do Amplify (igual aos Endereços)
            const { Auth } = await import('@aws-amplify/auth');
            const session = await Auth.currentSession();
            const token = session.getAccessToken().getJwtToken();
            
            const response = await fetch(`${BASE_URL}/${individualId}/contacts/${contactToDelete}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`  // ✅ Token do Amplify
                }
            });

            if (response.ok) {
                toast.success('Contato excluído!');
                await loadContacts();
            } else {
                toast.error('Erro ao excluir contato');
            }
        } catch (error) {
            toast.error('Erro ao excluir contato');
            console.error('Error deleting contact:', error);
        } finally {
            isLoading.value = false;
            isDeleteModalOpen.value = false;
            setContactToDelete(null);
        }
    };

    const loadContacts = async () => {
        try {
            // ✅ CORREÇÃO: Obter JWT token do Amplify (igual aos Endereços)
            const { Auth } = await import('@aws-amplify/auth');
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();  // ✅ IdToken para GET (igual endereços)
            
            const response = await fetch(`${BASE_URL}/${individualId}/contacts`, {
                headers: {
                    'x-api-key': API_KEY || '',
                    'Authorization': `Bearer ${token}`  // ✅ Token do Amplify
                }
            });

            if (response.ok) {
                const data = await response.json();
                onContactsChange(data.contacts);
            }
        } catch (error) {
            console.error('Error loading contacts:', error);
        }
    };

    const confirmDelete = (contactId: string) => {
        setContactToDelete(contactId);
        isDeleteModalOpen.value = true;
    };

    const formatContent = (type: string, content: string) => {
        if (['PHONE', 'MOBILE', 'WHATSAPP'].includes(type)) {
            // Format phone number
            const clean = content.replace(/\D/g, '');
            if (clean.length === 11) {
                return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
            } else if (clean.length === 10) {
                return `(${clean.slice(0, 2)}) ${clean.slice(2, 6)}-${clean.slice(6)}`;
            }
        }
        return content;
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TbPhone className="text-blue-600" />
                    Contatos
                </h3>
                <ActionButton
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2"
                >
                    <TbPlus size={16} />
                    Novo Contato
                </ActionButton>
            </div>

            {/* Lista de Contatos */}
            <div className="bg-white rounded-lg shadow">
                {contacts.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <TbPhone size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>Nenhum contato cadastrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tipo</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Contato</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Padrão</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Status</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {contacts.map((contact, index) => (
                                    <tr key={contact.id || index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                {getContactIcon(contact.type)}
                                                <span className="font-medium">
                                                    {getContactTypeLabel(contact.type)}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-sm">
                                                {formatContent(contact.type, contact.content)}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            {contact.isDefault && (
                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    Padrão
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                contact.isActive 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {contact.isActive ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    onClick={() => openModal(contact)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Editar"
                                                >
                                                    <TbEdit size={16} />
                                                </button>
                                                <button
                                                    onClick={() => confirmDelete(contact.id!)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Excluir"
                                                >
                                                    <TbTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Cadastro/Edição */}
            {isModalOpen.value && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold mb-4">
                            {editingContact ? 'Editar Contato' : 'Novo Contato'}
                        </h3>

                        <div className="space-y-4">
                            <SelectTextField
                                id="type"
                                label="Tipo *"
                                value={form.type}
                                onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                                items={contactTypes}
                            />

                            <TextEdit
                                id="content"
                                label="Contato *"
                                value={form.content}
                                onChange={(e) => setForm(prev => ({ ...prev, content: e.target.value }))}
                                placeholder={
                                    form.type === 'EMAIL' ? 'exemplo@email.com' :
                                    ['PHONE', 'MOBILE', 'WHATSAPP'].includes(form.type) ? '(11) 99999-9999' :
                                    'Digite o contato'
                                }
                            />

                            <div className="flex items-center gap-4">
                                <label className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={form.isDefault}
                                        onChange={(e) => setForm(prev => ({ ...prev, isDefault: e.target.checked }))}
                                        className="rounded"
                                    />
                                    <span className="text-sm">Contato padrão</span>
                                </label>

                                {['PHONE', 'MOBILE', 'WHATSAPP'].includes(form.type) && (
                                    <label className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            checked={form.isNationalNumber}
                                            onChange={(e) => setForm(prev => ({ ...prev, isNationalNumber: e.target.checked }))}
                                            className="rounded"
                                        />
                                        <span className="text-sm">Número nacional</span>
                                    </label>
                                )}
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <ActionButton
                                onClick={closeModal}
                                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </ActionButton>
                            <ActionButton
                                onClick={handleSubmit}
                                disabled={isLoading.value}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading.value ? 'Salvando...' : 'Salvar'}
                            </ActionButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen.value}
                onCancel={() => {
                    isDeleteModalOpen.value = false;
                    setContactToDelete(null);
                }}
                onConfirm={handleDelete}
                title="Excluir Contato"
                id={contactToDelete || ''}
                text="Tem certeza que deseja excluir este contato? Esta ação não pode ser desfeita."
            />
        </div>
    );
};

export default IndividualFormContacts;
